import { useState, useEffect, useRef, memo } from 'react';
import { motion } from 'framer-motion';
import { Copy, Check, Code, Eye } from 'lucide-react';
import mermaid from 'mermaid';
import { useTheme } from '../../hooks/useThemeToggle';

const initMermaid = (isDark) => {
  mermaid.initialize({
    startOnLoad: false,
    theme: isDark ? 'dark' : 'default',
    themeVariables: isDark ? {
      darkMode: true,
      background: '#0D1117',
      primaryColor: '#1a3a4a',
      primaryTextColor: '#E6EDF3',
      primaryBorderColor: '#1E4E8C',
      lineColor: '#8B949E',
      secondaryColor: '#161B22',
      tertiaryColor: '#0D1117',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '13px',
    } : {
      darkMode: false,
      background: '#FFFFFF',
      primaryColor: '#E0F2FE',
      primaryTextColor: '#0F172A',
      primaryBorderColor: '#1E4E8C',
      lineColor: '#94A3B8',
      secondaryColor: '#F8FAFC',
      tertiaryColor: '#F1F5F9',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '13px',
    },
    flowchart: { curve: 'basis', padding: 16 },
    sequence: { mirrorActors: false },
  });
};

let mermaidCounter = 0;

export const MermaidBlock = memo(({ code }) => {
  const containerRef = useRef(null);
  const [svg, setSvg] = useState('');
  const [error, setError] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [copied, setCopied] = useState(false);
  const idRef = useRef(`mermaid-${++mermaidCounter}-${Date.now()}`);
  const { theme } = useTheme();

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        initMermaid(theme === 'dark');
        // Generate unique ID for each render to avoid conflicts
        const renderId = `m-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
        const { svg: rendered } = await mermaid.render(renderId, code.trim());
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to render diagram');
          setSvg('');
        }
      }
    };
    render();
    return () => { cancelled = true; };
  }, [code, theme]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="my-3 rounded-xl overflow-hidden border border-nexus-border-subtle bg-nexus-bg-secondary"
      data-testid="mermaid-diagram"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-nexus-bg-tertiary/30 border-b border-nexus-border-subtle">
        <span className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary font-mono flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
          </svg>
          diagram
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowCode(prev => !prev)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors"
            data-testid="mermaid-toggle-code"
          >
            {showCode ? <Eye size={11} /> : <Code size={11} />}
            {showCode ? 'Diagram' : 'Code'}
          </button>
          <button
            onClick={handleCopy}
            className="p-1 rounded text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors"
            data-testid="mermaid-copy-code"
          >
            {copied ? <Check size={12} className="text-[#1E4E8C]" /> : <Copy size={12} />}
          </button>
        </div>
      </div>

      {/* Content */}
      {showCode ? (
        <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed text-nexus-text-secondary font-mono">
          {code.trim()}
        </pre>
      ) : error ? (
        <div className="p-4">
          <p className="text-xs text-red-400 mb-2">Diagram rendering error</p>
          <pre className="text-[11px] text-nexus-text-tertiary font-mono overflow-x-auto">{code.trim()}</pre>
        </div>
      ) : svg ? (
        <div
          ref={containerRef}
          className="p-4 flex justify-center overflow-x-auto [&_svg]:max-w-full"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="p-4 flex justify-center">
          <div className="flex items-center gap-2 text-xs text-nexus-text-tertiary">
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-4 h-4 border border-nexus-text-tertiary border-t-[#1E4E8C] rounded-full" />
            Rendering diagram...
          </div>
        </div>
      )}
    </motion.div>
  );
});
